/**
 * The meadow region and everything it grows — a peer of `city.ts`, and its own file for the same
 * reason: a region type is a subtree, not an entry in the region field's list.
 *
 * `scatter` USED TO SIT BETWEEN THEM. It was a group holding one knob and seven leaves, described
 * as "the region type that grows things" — which is the meadow. Merged, so a stamp is a child of
 * the region that grows it and the tree is one level shallower everywhere it matters.
 *
 * AND `buildingSpacing` HAS GONE WITH IT. It was a master knob deriving all three building grids
 * (×0.6 hut, ×1.0 house, ×1.4 tower — exactly the 30/50/70 the three leaves declare), so the tree
 * offered two controls for one number and the per-kind ones lost. Each building owns its spacing,
 * which is what the tree said all along.
 *
 * Defaults come from DEFAULT_STAMP_DISTRIBUTION; catalog.test.ts holds this file to them.
 */
import { DEFAULT_OAK_LARGE_SHAPE, MAX_CANOPY_R_M } from '../../stamps/types/treeOakLarge.js';
export const MEADOW = {
    kind: 'meadow',
    label: 'Meadow',
    blurb: 'The region type that grows things. The scatter LATTICE stays global — a tree is a pure '
        + 'function of its grid cell and the region under it, so it cannot move as you walk toward it.',
    capability: 'volume',
    maxLod: -1,
    expandAtLod: 5,
    children: ['trees', 'oaksLarge', 'rockSmall', 'rockMedium', 'rockLarge', 'buildingHut', 'buildingSmall', 'buildingTower'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'frequency', label: 'Frequency', share: true, min: 0, max: 100, step: 5, def: 50, unit: '%',
            desc: 'How common this region type is, against the others. Unticking it is a frequency of zero.' },
        { t: 'material', key: 'wallMaterial', label: 'Wall material', def: 'stone',
            desc: 'The colour this type’s stretch of boundary wall is drawn in.' },
    ],
};
/** One leaf per distribution the scatter actually runs, with that distribution's own numbers. */
function stampLeaf(kind, label, blurb, maxLod, gridSize, jitterPct, exclusionRadius, coveragePct = 100) {
    return {
        kind, label, blurb,
        capability: 'volume',
        maxLod,
        expandAtLod: -1,
        children: [],
        toggle: { key: 'enabled', def: true },
        params: [
            { t: 'number', key: 'gridSize', label: 'Spacing', min: 2, max: 200, step: 0.5, def: gridSize, unit: 'm',
                desc: 'Distance between grid cells — one candidate per cell. Higher = sparser.' },
            { t: 'number', key: 'jitter', label: 'Jitter', min: 0, max: 100, step: 5, def: jitterPct, unit: '%',
                desc: 'How far a candidate wanders inside its cell. 0 = a visible grid; 100 = fully scattered.' },
            { t: 'number', key: 'exclusionRadius', label: 'Clearance', min: 0, max: 40, step: 0.5, def: exclusionRadius, unit: 'm',
                desc: 'How much space this claims from anything placed after it.' },
            { t: 'number', key: 'coverage', label: 'Coverage', min: 5, max: 100, step: 5, def: coveragePct, unit: '%',
                desc: 'Share of grid cells that spawn. Below 100 the scatter clumps and clears.' },
        ],
    };
}
/**
 * The big oak's leaf: the scatter numbers every stamp has, plus the SHAPE KNOBS the model reads
 * (treeOakLarge.setOakLargeShape). Radius is capped at MAX_CANOPY_R_M, which is the scatter's
 * neighbour-walk bound rather than a taste — see the constant.
 */
function oaksLarge() {
    const leaf = stampLeaf('oaksLarge', 'Large oaks', 'A life-sized oak — sparse, placed before the rocks and trees, which keep clear of it.', 5, 40, 40, 8);
    const d = DEFAULT_OAK_LARGE_SHAPE;
    return {
        ...leaf,
        params: [
            ...leaf.params,
            { t: 'number', key: 'trunk', label: 'Trunk height', min: 2, max: 12, step: 0.5, def: d.trunkM, unit: 'm',
                desc: 'Ground to the crotch, where the bole divides. The tree is this plus the crown radius tall.' },
            { t: 'number', key: 'radius', label: 'Crown radius', min: 3, max: MAX_CANOPY_R_M, step: 0.5, def: d.radiusM, unit: 'm',
                desc: 'Radius of the crown sphere from the crotch. Capped by how far the scatter looks.' },
            { t: 'number', key: 'range', label: 'Canopy range', min: 30, max: 270, step: 10, def: d.rangeDeg, unit: 'deg',
                desc: 'Sweep of the crown from the crotch: 0 straight up, 180 to horizontal, more hangs below it.' },
            { t: 'number', key: 'density', label: 'Branch density (per m²)', min: 0.05, max: 1, step: 0.05, def: d.density,
                desc: 'Branch ends per square metre of crown surface. Higher is bushier, and costlier.' },
            { t: 'number', key: 'prune', label: 'Pruning', min: 0, max: 50, step: 1, def: d.prune * 100, unit: '%',
                desc: 'Share of the crown lost to missing limbs — the gaps in the canopy.' },
            { t: 'bool', key: 'leaves', label: 'Leaves', def: d.foliage,
                desc: 'Leaves on the branch ends, or the bare skeleton.' },
        ],
    };
}
export const STAMP_KINDS = [
    stampLeaf('trees', 'Trees', 'Oaks and pines share one grid and pick a type per point.', 5, 3.5, 60, 1, 50),
    oaksLarge(),
    stampLeaf('rockSmall', 'Small rocks', 'The densest scatter, and the last to be placed.', 5, 4.2, 35, 1, 50),
    stampLeaf('rockMedium', 'Medium rocks', '', 5, 7, 40, 1, 50),
    stampLeaf('rockLarge', 'Large rocks', '', 5, 17, 40, 1, 50),
    stampLeaf('buildingHut', 'Huts', 'Placed before the rocks and trees, which then keep clear of them.', 5, 30, 40, 15),
    stampLeaf('buildingSmall', 'Houses', '', 5, 50, 35, 20),
    stampLeaf('buildingTower', 'Towers', 'A rare landmark, and the first thing placed — it claims the most space.', 5, 70, 35, 25),
];
export const MEADOW_KINDS = [MEADOW, ...STAMP_KINDS];
//# sourceMappingURL=meadow.js.map